import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { PillarCard } from "@/components/calv/PillarCard";
import { CaseCard } from "@/components/calv/CaseCard";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { Callout } from "@/components/calv/Callout";
import { DeltaHero } from "@/components/calv/DeltaHero";
import { LogoReferences } from "@/components/calv/LogoReferences";

export const Route = createFileRoute("/leistungen/")({
  head: () => {
    const h = pageHead("services", "de");
    return { meta: h.meta, links: h.links };
  },
  component: LeistungenPage,
});

const engagement = [
  {
    label: "Mediabudget",
    title: "Ab 3.500 € / Monat",
    body: "Unter dieser Schwelle rechnet sich systemische Optimierung nicht. Wir empfehlen dann ehrlich andere Wege.",
  },
  {
    label: "Vorlauf",
    title: "Vier bis sechs Wochen",
    body: "Audit, Tracking-Setup und Daten-Kalibrierung brauchen Zeit. Versprechen, die kürzer sind, halten wir nicht.",
  },
  {
    label: "Mindestlaufzeit",
    title: "Vier Quartale",
    body: "Compound-Effekte zeigen sich erst nach drei Monaten. Vier Quartale geben den Hebeln Zeit, voll zu wirken.",
  },
  {
    label: "Vergütung",
    title: "Budget-unabhängig",
    body: "Festes Senior-Honorar plus Effizienzanteil — nie am Mediavolumen. So sind unsere Anreize gleichgerichtet mit Ihren.",
  },
];

function LeistungenPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow="Was wir tun"
          headline="Leistungen"
          lead="Performance Marketing, SEO und AI SEO, Conversion- und Web-Optimierung, Tracking, Strategie sowie Web- und Software-Entwicklung — als zusammenhängendes Gewerk, operativ betreut durch den Geschäftsführer."
          metaStrip={["Seit 2013", "GF operativ", "Vergütung unabhängig vom Ad-Budget"]}
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen" }]}
        />

        <Section
          number="02"
          kicker="Unser Ansatz"
          headline={<>Leistungen. <em>Eine gemeinsame Datenebene.</em></>}
          caption="Performance Marketing, SEO, Conversion-Optimierung, Tracking, Strategie und Entwicklung werden auf einer gemeinsamen Datenbasis geführt. Keine getrennten Reportings, keine internen Reibungen."
        >
          <RevealOnScroll>
            <div className="grid gap-4 md:grid-cols-3">
              <PillarCard cta="Mehr erfahren ↗" pillar="Performance Marketing" discipline="Performance Marketing" description="Search-, Social-Media- und Affiliate-Performance über Google Ads, Meta, TikTok, LinkedIn und Microsoft Ads. Datengeführt, ohne Black-Box-Versprechen." href="/leistungen/performance-marketing" />
              <PillarCard cta="Mehr erfahren ↗" pillar="Social Media Marketing" discipline="Social Media Marketing" description="Organic-Content, Paid-Verstärkung und Community-Substanz auf Meta, TikTok, LinkedIn und Pinterest — als gemeinsames Steuerungssystem. Plattform-Strategie, nicht nur Anzeigen-Schaltung." href="/leistungen/social-media-marketing" />
              <PillarCard cta="Mehr erfahren ↗" pillar="SEO & AI SEO" discipline="SEO & AI SEO" description="Organische Reichweite in Google, ChatGPT, Perplexity. Nachhaltig, kompoundierend." href="/leistungen/seo-ai-seo" />
              <PillarCard cta="Mehr erfahren ↗" pillar="GEO — Generative Engine Optimization" discipline="GEO" description="Als Quelle genannt werden in KI-Antworten — ChatGPT, Perplexity, AI Overviews. Messbar über Share of Answer, ohne Garantieversprechen." href="/leistungen/geo-generative-engine-optimization" />
              <PillarCard cta="Mehr erfahren ↗" pillar="Conversion & Web" discipline="CRO & Web" description="Conversion- und Web-Optimierung. Hypothesen-getrieben, architektonisch gedacht." href="/leistungen/conversion-web-optimierung" />
              <PillarCard cta="Mehr erfahren ↗" pillar="Tracking & Datenbasis" discipline="Tracking & Datenbasis" description="Datenfundament und Attribution. GA4, GTM, server-seitig, BigQuery — sauber aufgesetzt, damit Steuerung auf belastbaren Zahlen ruht." href="/leistungen/tracking-datenbasis" />
              <PillarCard cta="Mehr erfahren ↗" pillar="Strategie & Koordination" discipline="Strategie & Koordination" description="Roadmaps, die zur Operations-Realität passen. Senior-Reviews und 4-Augen-Prinzip auf jedem Mandat." href="/leistungen/strategie-koordination" />
              <PillarCard cta="Mehr erfahren ↗" pillar="Website-Entwicklung" discipline="Website-Entwicklung" description="Performante, conversion-optimierte Sites auf WordPress, WooCommerce, Shopware und Shopify." href="/leistungen/website-entwicklung" />
              <PillarCard cta="Mehr erfahren ↗" pillar="Entwicklung digitaler Dienste" discipline="Entwicklung digitaler Dienste" description="Tools, Integrationen, Datenpipelines — auf Kunden-Bedarf zugeschnitten statt von der Stange." href="/leistungen/entwicklung-digitaler-dienste" />
            </div>

            <div className="mt-16">
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Werkzeuge</div>
              <h3 className="mt-3 font-display text-2xl text-ink md:text-3xl">Eigene Tools statt Standard-Tooling.</h3>
              <div className="mt-6">
                <Foundation
                  title="Tools"
                  sub="Delta · picazzoo · GonzalAds + Custom-SaaS"
                  meta="Eigene Software statt Standard-Tooling"
                  href="/leistungen/tools"
                />
              </div>
            </div>

            <div className="mt-16">
              <CTABlock
                headline={<>Aus der Übersicht zum <em>konkreten Gespräch.</em></>}
                body="Diese neun Leistungen sind nicht isoliert — sie wirken im Zusammenspiel. Welche Kombination für Ihre konkrete Situation trägt, klären wir in einem kurzen Erstgespräch. Wir geben eine erste methodische Einordnung, ob und welche Leistungen zu Ihrer Situation passen — und sagen offen, wenn wir nicht die richtigen sind."
                primary={{ label: "Strategiegespräch anfragen", to: "/kontakt" }}
                secondary={{ label: "Oder: methodische Werkzeuge ohne Anmeldung testen", to: "/tools" }}
              />
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="03"
          variant="deep"
          kicker="Proprietäres Tool"
          headline={<>Delta — <em>Produktdaten als Hebel.</em></>}
          caption="Im E-Commerce ist die Performance-Frage fast immer eine Produktdaten-Frage. DELTA ist unser Feed-Management-Layer, der genau dort ansetzt — versioniert, plattformspezifisch, ohne externe Lizenzkosten."
        >
          <DeltaHero />
        </Section>

        <Section
          number="04"
          kicker="Muster, die wir wiederholt sehen"
          headline={<>Drei typische Konstellationen — <em>drei Hebel.</em></>}
          caption="Keine inszenierten Hero-Cases. Drei Muster, die in Kundenprojekten regelmäßig auftauchen — und wie wir sie auflösen."
        >
          <div className="grid gap-4 md:grid-cols-3">
            <CaseCard
              id="Muster I"
              category="E-Commerce · DACH"
              headline="Drei Disziplinen, drei Dienstleister, ein Reporting-Patchwork."
              body="SEA, SEO und Tracking laufen parallel, aber unkoordiniert. Wir überführen sie in eine gemeinsame Steuerung, setzen Tracking serverseitig neu auf und kalibrieren die Produktdaten über DELTA auf das Sortiment."
              stat={{ value: "20–40 %", label: "typische KUR-Verbesserung" }}
            />
            <CaseCard
              id="Muster II"
              category="B2B · SaaS"
              headline="Volumen wächst — qualifizierte Pipeline nicht."
              body="Lead-Scoring wird direkt an Google Ads zurückgespielt, Bottom-Funnel-Cluster werden über CRM-Daten gesteuert. Mediabudget bleibt — die Verteilung ändert sich."
              stat={{ value: "GF", label: "operativ am Konto" }}
            />
            <CaseCard
              id="Muster III"
              category="E-Commerce"
              headline="Wettbewerbsdrift wird erst im Monatsreport sichtbar."
              body="Auction-Insights und interne Margen-Korridore in einer Steuerung — Eingriff bevor die Ergebniskennzahl ausschlägt."
              stat={{ value: "operativ", label: "statt monatlich" }}
            />
          </div>
        </Section>

        <LogoReferences locale="de" number="05" />

        <Section
          number="06"
          variant="tinted"
          kicker="Engagement-Modell"
          headline={<>Wann eine Zusammenarbeit <em>funktioniert.</em></>}
          caption="Vier Spielregeln, die wir vor jeder Zusammenarbeit ansprechen — damit niemand auf Pump arbeitet."
        >
          <div className="grid gap-4 md:grid-cols-2">
            {engagement.map((e) => (
              <RevealOnScroll key={e.label}>
                <div className="h-full rounded-lg border border-rule bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">
                    {e.label}
                  </div>
                  <div className="mt-3 font-display text-xl text-ink">{e.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{e.body}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>

          <Callout>
            Wir verdienen nicht mehr, wenn Sie mehr ausgeben — und das ist Absicht.
          </Callout>

          <div className="mt-12">
            <CTABlock
              headline={<>Lassen Sie uns über Ihre <em>Performance</em> sprechen.</>}
              body="Wenn Sie wissen möchten, wo Ihr Online-Marketing aktuell Potenzial verschenkt – und wie sich das strukturiert ändern lässt, sprechen wir gerne miteinander."
              primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
              secondary={{ label: "Über Calvarius", to: "/ueber-uns" }}
            />
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}

function Foundation({
  title,
  sub,
  meta,
  href,
}: {
  title: string;
  sub: string;
  meta: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className="flex items-center justify-between gap-4 rounded-lg border border-rule bg-white p-6 transition-colors hover:border-cyan"
    >
      <div>
        <div className="font-display text-lg text-ink">{title}</div>
        <div className="mt-1 text-sm text-ink-3">{sub}</div>
      </div>
      <div className="hidden font-mono text-[11px] text-ink-4 md:block">{meta}</div>
    </a>
  );
}
